import { supabase } from '../lib/supabase';

export const fileService = {
  async getFilesByProject(projectId) {
    const { data, error } = await supabase?.from('file_content')?.select('*')?.eq('project_id', projectId)?.order('file_path', { ascending: true });
    
    if (error) throw error;
    
    // Convert snake_case to camelCase
    return data?.map(row => ({
      id: row?.id,
      projectId: row?.project_id,
      filePath: row?.file_path,
      fileName: row?.file_name,
      content: row?.content,
      fileSize: row?.file_size,
      fileType: row?.file_type,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async getFileByPath(projectId, filePath) {
    const { data, error } = await supabase?.from('file_content')?.select('*')?.eq('project_id', projectId)?.eq('file_path', filePath)?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      projectId: data?.project_id,
      filePath: data?.file_path,
      fileName: data?.file_name,
      content: data?.content,
      fileSize: data?.file_size,
      fileType: data?.file_type,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async createFile(file) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');
    
    // Verify user owns the project
    const { data: project } = await supabase?.from('projects')?.select('user_id')?.eq('id', file?.projectId)?.single();
    
    if (!project || project?.user_id !== user?.id) {
      throw new Error('Unauthorized');
    }
    
    const { data, error } = await supabase?.from('file_content')?.insert({
        project_id: file?.projectId,
        file_path: file?.filePath,
        file_name: file?.fileName,
        content: file?.content,
        file_size: file?.fileSize,
        file_type: file?.fileType
      })?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      projectId: data?.project_id,
      filePath: data?.file_path,
      fileName: data?.file_name,
      content: data?.content,
      fileSize: data?.file_size,
      fileType: data?.file_type,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async updateFile(fileId, updates) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');
    
    const dbUpdates = {
      updated_at: new Date()?.toISOString()
    };
    
    if (updates?.content !== undefined) dbUpdates.content = updates?.content;
    if (updates?.fileSize !== undefined) dbUpdates.file_size = updates?.fileSize;
    
    const { data, error } = await supabase?.from('file_content')?.update(dbUpdates)?.eq('id', fileId)?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      projectId: data?.project_id,
      filePath: data?.file_path,
      fileName: data?.file_name,
      content: data?.content,
      fileSize: data?.file_size,
      fileType: data?.file_type,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async deleteFile(fileId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');
    
    const { error } = await supabase?.from('file_content')?.delete()?.eq('id', fileId);
    
    if (error) throw error;
  },

  buildFileTree(files) {
    const root = [];
    const pathMap = {};
    
    files?.forEach(file => {
      const parts = file?.filePath?.split('/')?.filter(Boolean);
      let currentLevel = root;
      let currentPath = '';
      
      parts?.forEach((part, index) => {
        currentPath += '/' + part;
        const isFile = index === parts?.length - 1;
        
        if (!pathMap?.[currentPath]) {
          const node = {
            name: part,
            path: currentPath,
            type: isFile ? 'file' : 'folder'
          };
          
          if (isFile) {
            node.content = file?.content;
            node.size = file?.fileSize;
            node.fileType = file?.fileType;
            node.id = file?.id;
          } else {
            node.children = [];
          }
          
          pathMap[currentPath] = node;
          currentLevel?.push(node);
        }
        
        if (!isFile) {
          currentLevel = pathMap?.[currentPath]?.children;
        }
      });
    });
    
    return root;
  }
};

export default fileService;